import { getIsStatutoryBenefitType } from "shared/types/BenefitTypes";
import { getIsWellthyValuedAddedServiceAvailable, type Client } from "shared/types/Client";
import { eifStepNavigation, type EIFSubStepId, type EIFStepId } from "shared/types/EIF";
import {
  getClientCoveragesThatAreAlwaysSentToEmployeesHome,
  getPlansWhereCanSetClaimsCheckLocations,
} from "shared/types/Plan";
import {
  getIsClaimPaymentsReimbursementCoverage,
  getIsCoverageSupportedInClassBuilder,
  getIsPFMLCoverage,
  getIsStatutoryCoverage,
} from "shared/types/SlfCoverages";
import { getShouldAskAgeQuestionForSpouseDependentLife } from "shared/utils/EIF/getShouldAskAgeQuestionForSpouseDependentLife";
import { getTerminationOfInsuranceRelevantPlans } from "shared/utils/EIF/getTerminationOfInsuranceRelevantPlans";
import { getPlansWhereCanSetMonthlyClaimsLocations } from "shared/utils/EIF/monthlyClaimsReportsAndEOBs";
import { getAllowedPreviousCoverages, getEAPShouldBeHidden } from "shared/utils/client";
import { exhaustiveCheckFail } from "shared/utils/exhaustiveCheck";
import { assertIsDefined, unique } from "shared/utils/utils";
import type { ClientFeatureToggles } from "shared/types/Toggles";

export function getIsSubStepApplicable(args: {
  eifSubStepId: EIFSubStepId;
  client: Client;
  featureToggles: ClientFeatureToggles;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "tax-id">;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "subsidiaries-and-affiliates">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "current-sun-life-policies">;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "previous-coverage-information">;
  client: Client;
}): boolean;
// Plan Administrators & Billing
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "plan-administrators">;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "billing-preferences">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "claims-payment-reimbursement">;
  client: Client;
  featureToggles: ClientFeatureToggles;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "claims-check-mailing-locations">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "monthly-claims-reports-and-eobs">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "malpractice-reimbursements">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "class-builder">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "non-class-benefits-preferences">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "fli-preferences">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "confirm-policy-anniversary">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "additional-waiting-period-rules">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "rehire-provision">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "union-members-and-domestic-partners">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "changes-during-annual-enrollment">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "termination-of-insurance">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "age-rules">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "other-changes-to-coverage">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "employee-certificate">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "section-125">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "erisa">;
  client: Client;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: Extract<EIFSubStepId, "value-added-services">;
  client: Client;
  featureToggles: ClientFeatureToggles;
}): boolean;
export function getIsSubStepApplicable(args: {
  eifSubStepId: EIFSubStepId;
  client?: Client;
  featureToggles?: ClientFeatureToggles;
}) {
  const { eifSubStepId } = args;
  switch (eifSubStepId) {
    // Company Details
    case "tax-id":
    case "current-sun-life-policies":
      return true;

    case "previous-coverage-information": {
      assertIsDefined(args.client, "args.client");
      const previousCoverages = getAllowedPreviousCoverages(args.client);
      const clientHasAvailablePreviousCoverage = previousCoverages.length > 0;
      return clientHasAvailablePreviousCoverage;
    }

    case "subsidiaries-and-affiliates": {
      assertIsDefined(args.client, "args.client");
      const hasStatutoryBenefits = args.client.plans.some((p) =>
        getIsStatutoryBenefitType(p.benefitType),
      );
      const hasASODental = args.client.plans.some((p) => p.benefitType === "ASO_DENTAL");
      return hasStatutoryBenefits || hasASODental;
    }

    // Plan Administrators & Billing
    case "plan-administrators":
      return true;
    case "billing-preferences": {
      assertIsDefined(args.client, "args.client");
      if (args.client.policies.length === 0) return false;
      if (args.client.allPoliciesSlfCoverages && args.client.allPoliciesSlfCoverages.length === 0)
        return false;

      const anyPolicyIsApplicable = args.client.policies.some((policy) => {
        if (getEAPShouldBeHidden(policy.eapIsBlendedWith)) {
          const EAPIsOnlyBenefitOnThePolicy =
            policy.slfCoverages &&
            policy.slfCoverages.length === 1 &&
            policy.slfCoverages[0] === "Employee Assistance Program";
          const shouldPolicyHaveBills = !EAPIsOnlyBenefitOnThePolicy;
          return shouldPolicyHaveBills;
        }
        return true;
      });
      return anyPolicyIsApplicable;
    }
    case "claims-payment-reimbursement": {
      assertIsDefined(args.client, "args.client");
      assertIsDefined(args.featureToggles, "args.featureToggles");

      if (
        args.client.policies.length === 0 ||
        args.client.allPoliciesSlfCoverages == null ||
        args.client.allPoliciesSlfCoverages.length === 0
      ) {
        return false;
      }

      const { client } = args;

      const isApplicable = args.client.allPoliciesSlfCoverages.some((coverage) =>
        getIsClaimPaymentsReimbursementCoverage(coverage, client.plans),
      );
      return isApplicable;
    }
    case "claims-check-mailing-locations": {
      assertIsDefined(args.client, "args.client");
      const relevantPlans = getPlansWhereCanSetClaimsCheckLocations(args.client.plans);
      const clientCoveragesThatAreAlwaysSentToEmployeesHome =
        getClientCoveragesThatAreAlwaysSentToEmployeesHome(args.client.plans);
      const isClaimsCheckPlanSentToEEOnly =
        relevantPlans.length === 0 && clientCoveragesThatAreAlwaysSentToEmployeesHome.length > 0;
      const isApplicable = relevantPlans.length > 0 || isClaimsCheckPlanSentToEEOnly;
      return isApplicable;
    }
    case "monthly-claims-reports-and-eobs": {
      assertIsDefined(args.client, "args.client");
      const relevantPlans = getPlansWhereCanSetMonthlyClaimsLocations(args.client.plans);
      const isApplicable = relevantPlans.length > 0;
      return isApplicable;
    }
    case "malpractice-reimbursements": {
      assertIsDefined(args.client, "args.client");
      const isHPS = args.client.healthcareProfessionalsSegment;
      const hasLTD = args.client.allPoliciesSlfCoverages?.some(
        (coverage) => coverage === "Long Term Disability",
      );
      const isApplicable = Boolean(
        isHPS && hasLTD && args.client.malpracticeReimbursementRider === "YES",
      );
      return isApplicable;
    }

    // Plan Configuration & Eligibility
    case "class-builder": {
      assertIsDefined(args.client, "args.client");
      const { client } = args;
      const clientHasClassBuilderCoverages =
        client.allPoliciesSlfCoverages?.some((coverage) =>
          getIsCoverageSupportedInClassBuilder(coverage, client.plans),
        ) ?? false;
      const isApplicable = clientHasClassBuilderCoverages;
      return isApplicable;
    }

    case "non-class-benefits-preferences": {
      assertIsDefined(args.client, "args.client");
      const clientHasPFMLCoverages =
        args.client.allPoliciesSlfCoverages?.some((coverage) => getIsPFMLCoverage(coverage)) ??
        false;
      const clientHasStatutoryCoverages =
        args.client.allPoliciesSlfCoverages?.some((coverage) => getIsStatutoryCoverage(coverage)) ??
        false;
      const isApplicable = clientHasPFMLCoverages || clientHasStatutoryCoverages;
      return isApplicable;
    }

    case "fli-preferences": {
      assertIsDefined(args.client, "args.client");
      const clientHasFLICoverages =
        args.client.allPoliciesSlfCoverages?.some(
          (coverage) => coverage === "Family Leave Insurance",
        ) ?? false;
      const isApplicable = clientHasFLICoverages;
      return isApplicable;
    }

    // Other Contract Details
    case "confirm-policy-anniversary": {
      assertIsDefined(args.client, "args.client");
      const onlyStatutoryCoverages = getOnlyStatutoryCoverages({ client: args.client });
      if (onlyStatutoryCoverages) return false;

      return true;
    }
    case "additional-waiting-period-rules":
    case "rehire-provision":
    case "union-members-and-domestic-partners":
    case "other-changes-to-coverage":
    case "employee-certificate":
    case "section-125":
    case "erisa": {
      assertIsDefined(args.client, "args.client");
      const onlyStatutoryCoverages = getOnlyStatutoryCoverages({ client: args.client });
      if (onlyStatutoryCoverages) return false;

      const onlyPFMLCoverages = getOnlyPFMLCoverages({ client: args.client });
      const isApplicable = !onlyPFMLCoverages;
      return isApplicable;
    }
    case "changes-during-annual-enrollment": {
      assertIsDefined(args.client, "args.client");
      const onlyStatutoryCoverages = getOnlyStatutoryCoverages({ client: args.client });
      if (onlyStatutoryCoverages) return false;

      const onlyPFMLCoverages = getOnlyPFMLCoverages({ client: args.client });
      if (onlyPFMLCoverages) return false;

      const slfCoverageHasDental = args.client.allPoliciesSlfCoverages?.includes("Dental");
      const slfCoverageHasVision = args.client.allPoliciesSlfCoverages?.includes("Vision");
      const hasDentalOrVision = slfCoverageHasDental || slfCoverageHasVision;
      const isApplicable = !!(
        args.client.allBenefitsNonContributory !== "YES" || hasDentalOrVision
      );
      return isApplicable;
    }
    case "termination-of-insurance": {
      assertIsDefined(args.client, "args.client");
      const onlyStatutoryCoverages = getOnlyStatutoryCoverages({ client: args.client });
      if (onlyStatutoryCoverages) return false;

      const relevantPlans = getTerminationOfInsuranceRelevantPlans(args.client.plans);
      const benefitTypes = unique(relevantPlans.map((p) => p.benefitType));
      const isApplicable = benefitTypes.length > 0;
      return isApplicable;
    }
    case "age-rules": {
      assertIsDefined(args.client, "args.client");
      const onlyStatutoryCoverages = getOnlyStatutoryCoverages({ client: args.client });
      if (onlyStatutoryCoverages) return false;

      const onlyPFMLCoverages = getOnlyPFMLCoverages({ client: args.client });
      if (onlyPFMLCoverages) return false;

      const isAgeQuestionForSpouseDependentLifeApplicable =
        getShouldAskAgeQuestionForSpouseDependentLife(args.client);

      const isApplicable =
        (args.client.ageRules?.length ?? 0) > 0 || isAgeQuestionForSpouseDependentLifeApplicable;
      return isApplicable;
    }

    case "value-added-services": {
      // hide this section if:
      // client only has pfml coverages ||
      // client has no life coverage ||
      // client has no supp health coverage ||
      // NONE is selected for CI VAS and CI is the only relevant benefit for VAS section ||
      // situs state is VT and effective date is after 1/1/26

      assertIsDefined(args.client, "args.client");
      assertIsDefined(args.featureToggles, "args.featureToggles");

      const onlyStatutoryCoverages = getOnlyStatutoryCoverages({ client: args.client });
      if (onlyStatutoryCoverages) return false;

      const onlyPFMLCoverages = getOnlyPFMLCoverages({ client: args.client });
      if (onlyPFMLCoverages) return false;

      const showValueAddedServicesLifeCoverage = shouldShowValueAddedServicesLifeCoverage(
        args.client,
      );
      const showValueAddedServicesSuppHealthCoverage =
        shouldShowValueAddedServicesSuppHealthCoverage(args.client);
      const isApplicable =
        showValueAddedServicesLifeCoverage || showValueAddedServicesSuppHealthCoverage;

      return isApplicable;
    }
    default:
      exhaustiveCheckFail(eifSubStepId);
  }
}

// https://maxwellhealth.atlassian.net/browse/TB-5831
function getOnlyPFMLCoverages(args: { client: Client }) {
  if (args.client.allPoliciesSlfCoverages == null) return false;
  const onlyPFMLCoverages =
    args.client.allPoliciesSlfCoverages.length > 0 &&
    args.client.allPoliciesSlfCoverages.every((coverage) => getIsPFMLCoverage(coverage));
  return onlyPFMLCoverages;
}

// https://maxwellhealth.atlassian.net/browse/TB-6620
function getOnlyStatutoryCoverages(args: { client: Client }) {
  if (args.client.allPoliciesSlfCoverages == null) return false;
  const everyCoverageIsStatutory =
    args.client.allPoliciesSlfCoverages.length > 0 &&
    args.client.allPoliciesSlfCoverages.every(getIsStatutoryCoverage);
  return everyCoverageIsStatutory;
}

export const shouldHideWithSitusStateVermont = (client: Client): boolean => {
  const isWellthyValuedAddedServiceAvailable = getIsWellthyValuedAddedServiceAvailable(client);
  if (!isWellthyValuedAddedServiceAvailable) {
    return false;
  }

  const hasSitusStateVT = client.policies.some(
    ({ issuedStateSitus, slfCoverages }) =>
      issuedStateSitus?.includes("VT") &&
      (slfCoverages?.includes("Voluntary Life") || slfCoverages?.includes("Basic Life")),
  );
  return hasSitusStateVT;
};

export const shouldShowValueAddedServicesLifeCoverage = (client: Client) => {
  const allCoverages = client.allPoliciesSlfCoverages ?? [];
  const hasDisallowedSitusStateNewYork = client.policies.some(
    ({ issuedStateSitus, slfCoverages }) =>
      issuedStateSitus?.includes("NY") &&
      (slfCoverages?.includes("Voluntary Life") || slfCoverages?.includes("Basic Life")),
  );

  const hasDisallowedSitusStateVermont = shouldHideWithSitusStateVermont(client);

  const hasVoluntaryOrBasicLifeCoverage = allCoverages.some(
    (coverage) => coverage === "Voluntary Life" || coverage === "Basic Life",
  );

  const showValueAddedServicesLifeCoverage = Boolean(
    !hasDisallowedSitusStateNewYork &&
      !hasDisallowedSitusStateVermont &&
      hasVoluntaryOrBasicLifeCoverage,
  );

  return showValueAddedServicesLifeCoverage;
};

export const shouldShowValueAddedServicesSuppHealthCoverage = (client: Client) => {
  const allCoverages = client.allPoliciesSlfCoverages ?? [];
  const hasDisallowedSitusStateNewYork = client.policies.some(
    ({ issuedStateSitus, slfCoverages }) =>
      issuedStateSitus?.includes("NY") &&
      (slfCoverages?.includes("Accident") || slfCoverages?.includes("Critical Illness")),
  );
  const hasAccidentCoverage = allCoverages.some((coverage) => coverage === "Accident");
  const hasCriticalIllnessCoverage = allCoverages.some(
    (coverage) => coverage === "Critical Illness",
  );
  const criticalIllnessValueAddedServicesIsNone =
    client.criticalIllnessValueAddedService === "NONE";

  const showForCriticalIllness =
    hasCriticalIllnessCoverage && !criticalIllnessValueAddedServicesIsNone;

  const showValueAddedServicesSuppHealthCoverage = Boolean(
    !hasDisallowedSitusStateNewYork && (hasAccidentCoverage || showForCriticalIllness),
  );

  return showValueAddedServicesSuppHealthCoverage;
};

export function getAtLeastOneSubStepIsApplicable(
  eifStepId: EIFStepId,
  client: Client,
  featureToggles: ClientFeatureToggles,
) {
  if (eifStepId === "review-&-submit") return true;

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const subStepsInThisStep = Object.keys(eifStepNavigation[eifStepId]) as EIFSubStepId[];

  const atLeastOneSubStepIsApplicable = subStepsInThisStep.some((eifSubStepId) => {
    const isSubStepApplicable = getIsSubStepApplicable({
      eifSubStepId,
      client,
      featureToggles,
    });

    return isSubStepApplicable;
  });
  return atLeastOneSubStepIsApplicable;
}
